import { Container, Link, Message, Stack, Text } from "basis";
import { Separator } from "components/Separator/Separator";
import { AddressFormat } from "library/api/address/types.generated";
import { Address } from "../components/Address";
import { Email } from "../components/Email";
import { useNav } from "../hooks/useNav";
import { AccountWithPersonalDetails } from "../types";
import { isMailingAddressSameAsResidential } from "../utils";
import { PhoneNumbers } from "./PhoneNumbers";

type Props = {
  accountWithPersonalDetails: AccountWithPersonalDetails;
};

export const AccountSectionContent = ({
  accountWithPersonalDetails,
}: Props) => {
  const { account, status, personalDetails } = accountWithPersonalDetails;

  const { navigate } = useNav();

  if (account.type === "PERSONAL_LOAN") {
    return (
      <Text testId="personal-load-content">
        Managing personal details is not available for personal loans.
        <br />
        {`Please `}
        <Link href="/contact" newTab={false}>
          contact Latitude
        </Link>
        {` to update your details.`}
      </Text>
    );
  }

  if (status === "JOINT_ACCOUNT") {
    return (
      <Text testId="joint-account-content">
        Due to the status of your account, your address details are not
        available.
        <br />
        {`Please `}
        <Link href="/contact" newTab={false}>
          contact Latitude
        </Link>
        {` to update your details.`}
      </Text>
    );
  }

  if (status === "SOMETHING_WENT_WRONG" || !personalDetails) {
    return (
      <Text testId="something-went-wrong">
        We've encountered an unexpected problem.
        <br />
        {`Please `}
        <Link href="/contact" newTab={false}>
          contact Latitude
        </Link>
        {` to update your details.`}
      </Text>
    );
  }

  const canEdit = "canEdit" in personalDetails && personalDetails.canEdit;
  const { residential, mailing } = personalDetails;

  return (
    <Container padding="2 0">
      <Stack gap="8">
        {!canEdit && personalDetails?.type === "full" && (
          <Message
            testId="hide-personal-details"
            severity="info-or-minor"
            bg="secondary.lightBlue.t25"
          >
            Due to the status of your account, your personal details are not
            able to be updated via this page. To update your details, please
            contact Latitude{" "}
            <Link href="/contact" newTab={false}>
              contact support
            </Link>
            .
          </Message>
        )}
        {accountWithPersonalDetails.personalDetails?.type ===
          AddressFormat.masked && (
          <Message
            testId="unable-to-disclose"
            severity="info-or-minor"
            bg="secondary.lightBlue.t25"
          >
            Due to the status of your account, we are unable to disclose your
            personal details. Please contact Latitude{" "}
            <Link href="/contact" newTab={false}>
              contact support
            </Link>
            .
          </Message>
        )}
        <Email
          email={personalDetails.emailAddress as string}
          canEdit={canEdit}
          accountWithPersonalDetails={accountWithPersonalDetails}
        />
        <Separator />
        <PhoneNumbers
          accountWithPersonalDetails={accountWithPersonalDetails}
          canEdit={canEdit}
        />
        <Separator />
        <Stack gap="5" testId="residential-address">
          <Text as="h3" textStyle="heading5" color="primary.blue.t100">
            Residential address
          </Text>
          <Address address={residential} />
          {canEdit && (
            <Link
              href="#"
              newTab={false}
              onClick={(e) => {
                e.preventDefault();
                navigate("/residential-address", {
                  state: {
                    account,
                    fullPersonalDetails:
                      accountWithPersonalDetails.personalDetails,
                  },
                });
              }}
            >
              Edit
            </Link>
          )}
        </Stack>
        <Separator />
        <Stack gap="5" testId="mailing-address">
          <Text as="h3" textStyle="heading5" color="primary.blue.t100">
            Mailing address
          </Text>
          {isMailingAddressSameAsResidential(accountWithPersonalDetails) ? (
            <Text>Same as residential address</Text>
          ) : (
            mailing && <Address address={mailing} />
          )}
          {canEdit && (
            <Link
              href="#"
              newTab={false}
              onClick={(e) => {
                e.preventDefault();
                navigate("/mailing-address", {
                  state: {
                    account,
                    fullPersonalDetails:
                      accountWithPersonalDetails.personalDetails,
                  },
                });
              }}
            >
              Edit
            </Link>
          )}
        </Stack>
      </Stack>
    </Container>
  );
};
